"use client";
import { Box, Button, Container, Divider, Typography } from "@mui/material";
import type React from "react";

import RateYourExperience from "./RateYourExperience.tsx";
import QuestionsBank from "./QuestionsBank.tsx";
import OtherDetailsScoreCard from "./OtherDetailsScoreCard.tsx";
import { useNavigate } from "react-router-dom";
import SyncIcon from "@mui/icons-material/Sync"

interface PreviewScoreCardProps {
  // Data states
  date: Date | null;
  ratings: Record<string, number>;
  starRatings: any[];
  staffMemberDesc: string;
  textInputs: any;
  briefJson: any[];
  addOnQuestions: any;
  addOnQuestionsData: any[];
  reportDetailsHeader: any;
  uploadedFiles: any;
  answers: any;
  invoice: {
    food: number;
    bar: number;
    gaming: number;
    total: number;
  };
  reportDetails: any;
  currentCategoryIndex: number;

  // Functions
  setDate: (date: Date | null) => void;
  setStaffMemberDesc: (desc: string) => void;
  handleRatingChange: (service: string, value: number) => void;
  handleTextChange: (field: string, value: string) => void;
  handleAddOnQuestionChange: (field: string, value: string) => void;
  setAnswers: (answers: any) => void;
  setUploadedFiles: (files: any) => void;
  handleAnswerChange: (questionId: any, value: any) => void;
  handleCommentChange: (questionId: number, comment: string) => void;
  handleFileUpload: (
    event: React.ChangeEvent<HTMLInputElement>,
    section: string,
    questionId?: number,
    reportDetailId?: number
  ) => void;
  handlePreviousCategory: () => void;
  handleNextCategory: () => void;
  handleRemoveFile: (
    section: string,
    reportId: number,
    imageUrl?: string,
    questionId?: number
  ) => void;
  setInvoice: (invoice: any) => void;
  setPreviousStep: () => void;
  
  
  // Navigation
  onSubmit: () => void;
  onCancel: () => void;
  onSyncDataToServer: () => void;
  validationErrors: any[];
  showPreview: any;
  handlePreviousStep: ()=>void;
  setShow: ()=>void;
}

export default function PreviewScoreCard({
  // Data states
  date,
  ratings,
  starRatings,
  staffMemberDesc,
  textInputs,
  briefJson,
  addOnQuestions,
  addOnQuestionsData,
  reportDetailsHeader,
  uploadedFiles,
  answers,
  invoice,
  reportDetails,
 handlePreviousStep,
  currentCategoryIndex,

  // Functions
  setDate,
  setStaffMemberDesc,
  handleRatingChange,
  handleTextChange,
  handleAddOnQuestionChange,
  setAnswers,
  setUploadedFiles,
  handleAnswerChange,
  handleCommentChange,
  handleFileUpload,
  handlePreviousCategory,
  handleNextCategory,
  handleRemoveFile,
  setInvoice,

  // Navigation
  onSubmit,
  onCancel,
  onSyncDataToServer,
  validationErrors,
  setShow,
  showPreview
}: PreviewScoreCardProps) {
  const navigate = useNavigate();
  return (
    <>
      <Box className="scoreCardContainer" sx={{ height: "100vh", display: "flex", flexDirection: "column", background: "#fff" }}>

        {/* Top Fixed Header */}
        <Box
          sx={{
            flexShrink: 0,
            px: 1,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#fff",
            zIndex: 1000,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Button
            variant="text"
            sx={{
              color: "#ADB792",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "14px",
              textTransform: "none",
            }}
            onClick={() => navigate(-1)}
          >
            <span style={{ fontSize: "20px", color: "#ADB792" }}>&larr;</span>
            <span style={{ textDecoration: "underline", fontSize: "18px" }}>Exit report</span>
          </Button>

          <Button
            variant="text"
            onClick={onSyncDataToServer}
            sx={{
              color: "#000000",
              fontSize: "18px",
              fontWeight: "bold",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
            startIcon={<SyncIcon sx={{ fontSize: "16px", color: "#666" }} />}
          >
            Sync Now
          </Button>
        </Box>

        {/* Scrollable Middle Content */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2 }}>

          <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Section 1: Rate Your Experience */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  bgcolor: "#ADB7921A",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  p: 3,
                }}
              >
                <RateYourExperience
                  date={date}
                  ratings={ratings}
                  starRatings={starRatings}
                  staffMemberDesc={staffMemberDesc}
                  setStaffMemberDesc={setStaffMemberDesc}
                  textInputs={textInputs}
                  reportGroup={reportDetails?.reportHeader?.allocationClientGroup}
                  briefJson={briefJson}
                  shopTypeDetail={reportDetails?.shopTypeDetail || []}
                  addOnQuestions={addOnQuestions}
                  addOnQuestionsData={addOnQuestionsData}
                  reportDetailsHeader={reportDetailsHeader}
                  reportOtherDetails={reportDetails?.reportOtherDetails}
                  setDate={setDate}
                  venueName={reportDetails?.venueName}
                  clientGroup={reportDetails?.reportHeader?.allocationClientGroup}
                  onRatingChange={handleRatingChange}
                  onTextChange={handleTextChange}
                  onAddOnQuestionChange={handleAddOnQuestionChange}
                  validationErrors={[]}
                />
              </Box>
            </Box>

            {/* Section 2: Questions Bank */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  bgcolor: "#ADB7921A",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  p: 3,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 2 }}>
                  Questions
                </Typography>
                <Divider sx={{ borderBottomWidth: "2px", borderColor: "#00000033" }} />
                {/* {reportDetails?.questions?.map((category: any, index: number) => (
                  <Box key={index} sx={{ mb: 4 }}>
                    <QuestionsBank
                      questionsData={reportDetails}
                      currentCategoryIndex={index}
                      uploadedFiles={uploadedFiles}
                      answers={answers}
                      reportGroup={reportDetails?.reportHeader?.allocationClientGroup}
                      setAnswer={setAnswers}
                      clientGroup={reportDetails?.reportHeader?.allocationClientGroup}
                      setUploadedFiles={setUploadedFiles}
                      onAnswerChange={handleAnswerChange}
                      onCommentChange={handleCommentChange}
                      onFileUpload={handleFileUpload}
                      onPreviousCategory={handlePreviousCategory}
                      onNextCategory={handleNextCategory}
                      onRemoveFile={handleRemoveFile}
                      validationErrors={[]}
                      showPreview={showPreview}
                    />
                    {index < reportDetails.questions.length - 1 && (
                      <Divider sx={{ my: 3 }} />
                    )}
                  </Box>
                ))}
              
                  <Box  sx={{ mb: 4 }}>
                    <QuestionsBank
                      questionsData={reportDetails}
                      // currentCategoryIndex={index}
                      uploadedFiles={uploadedFiles}
                      answers={answers}
                      reportGroup={reportDetails?.reportHeader?.allocationClientGroup}
                      setAnswer={setAnswers}
                      clientGroup={reportDetails?.reportHeader?.allocationClientGroup}
                      setUploadedFiles={setUploadedFiles}
                      onAnswerChange={handleAnswerChange}
                      onCommentChange={handleCommentChange}
                      onFileUpload={handleFileUpload}
                      onPreviousCategory={handlePreviousCategory}
                      onNextCategory={handleNextCategory}
                      onRemoveFile={handleRemoveFile}
                      validationErrors={[]}
                      showPreview={showPreview}
                    />
                    {index < reportDetails.questions.length - 1 && (
                      <Divider sx={{ my: 3 }} />
                    )}
                  </Box> */}

                {reportDetails?.reportHeader?.allocationClientGroup !== "ALDI" && reportDetails?.reportHeader?.allocationClientGroup !== "TRANSPORT"  ? (
                  // First one
                  reportDetails?.questions?.map((category: any, index: number) => (
                    <Box key={index} sx={{ mb: 4 }}>
                      <QuestionsBank
                        questionsData={reportDetails}
                        currentCategoryIndex={index}
                        uploadedFiles={uploadedFiles}
                        answers={answers}
                        reportGroup={reportDetails?.reportHeader?.allocationClientGroup}
                        setAnswer={setAnswers}
                        clientGroup={reportDetails?.reportHeader?.allocationClientGroup}
                        setUploadedFiles={setUploadedFiles}
                        onAnswerChange={handleAnswerChange}
                        onCommentChange={handleCommentChange}
                        onFileUpload={handleFileUpload}
                        onPreviousCategory={handlePreviousCategory}
                        onNextCategory={handleNextCategory}
                        onRemoveFile={handleRemoveFile}
                        validationErrors={[]}
                        showPreview={showPreview}
                      />
                      {index < reportDetails.questions.length - 1 && (
                        <Divider sx={{ my: 3 }} />
                      )}
                    </Box>
                  ))
                ) : (
                  // Second one
                  <Box sx={{ mb: 4 }}>
                    <QuestionsBank
                      questionsData={reportDetails}
                      uploadedFiles={uploadedFiles}
                      answers={answers}
                      reportGroup={reportDetails?.reportHeader?.allocationClientGroup}
                      setAnswer={setAnswers}
                      clientGroup={reportDetails?.reportHeader?.allocationClientGroup}
                      setUploadedFiles={setUploadedFiles}
                      onAnswerChange={handleAnswerChange}
                      onCommentChange={handleCommentChange}
                      onFileUpload={handleFileUpload}
                      onPreviousCategory={handlePreviousCategory}
                      onNextCategory={handleNextCategory}
                      onRemoveFile={handleRemoveFile}
                      validationErrors={[]}
                      showPreview={showPreview}
                    />
                  </Box>
                )}

              </Box>
            </Box>

            {/* Section 3: Other Details */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  bgcolor: "#ADB7921A",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  p: 3,
                }}
              >
                <OtherDetailsScoreCard
                  invoice={invoice}
                  setInvoice={setInvoice}
                  reportDetails={reportDetails}
                  uploadedFiles={uploadedFiles}
                  onFileUpload={handleFileUpload}
                  onRemoveFile={handleRemoveFile}
                  validationErrors={[]}
                />
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Bottom Fixed Footer */}
        <Box
          sx={{
            flexShrink: 0,
            p: 2,
            backgroundColor: "#fff",
            borderTop: "1px solid #ccc",
            textAlign: "center",
          }}
        >
          <Button
            variant="outlined"
            onClick={()=>{handlePreviousStep(); setShow();}}
            sx={{
              borderColor: "#ADB792",
              color: "#ADB792",
              fontWeight: "500",
              px: 4,
              mr: 2,
            }}
          >
            Back to Edit
          </Button>
          <Button
            variant="contained"
            className="button-primary py-2 px-3"
            onClick={onSubmit}
            sx={{ px: 4, fontSize: "14px !important" }}
          >
            Submit Report
          </Button>
        </Box>
      </Box>
    </>
  );
}
